import {
  GamepadInputAdapter,
  InputBuffer,
  KeyboardInputAdapter,
  TouchInputAdapter,
  type GamepadSnapshot,
  type InputFrame,
  type KeyboardBindings,
} from '../input/input-contract';
import type { ArenaDirection } from '../domain/arena-rules';
import { BrowserPauseCoordinator, PauseController } from './pause-controller';

interface KeyboardEventData extends Event {
  readonly code: string;
  readonly repeat: boolean;
}

interface ClosestTarget {
  closest?: (selector: string) => unknown;
}

export const shouldIgnoreGlobalKeyboard = (
  _code: string,
  target: ClosestTarget | null,
): boolean => {
  return (
    target?.closest?.('button, input, select, textarea, summary, [contenteditable="true"]') != null
  );
};

export interface CombatIntent {
  readonly facing: ArenaDirection;
  readonly attackDirection?: ArenaDirection;
  readonly dashDirection?: ArenaDirection;
}

export const resolveCombatIntent = (
  frame: InputFrame,
  lastFacing: ArenaDirection,
): CombatIntent => {
  const facing =
    frame.movement.x === 0 && frame.movement.y === 0 ? lastFacing : frame.movement;
  return {
    facing,
    ...(frame.actions.includes('attack') ? { attackDirection: facing } : {}),
    ...(frame.actions.includes('dash') ? { dashDirection: facing } : {}),
  };
};

export interface VisibilityEventTarget extends EventTarget {
  readonly hidden: boolean;
}

export class BrowserGameRuntime {
  readonly input = new InputBuffer();
  readonly pause = new PauseController();
  readonly keyboard: KeyboardInputAdapter;
  readonly controller = new GamepadInputAdapter(this.input);
  readonly touch = new TouchInputAdapter(this.input);
  readonly #pauseCoordinator = new BrowserPauseCoordinator(this.pause);
  #detach: (() => void) | undefined;

  public constructor(bindings?: KeyboardBindings) {
    this.keyboard = new KeyboardInputAdapter(this.input, bindings);
  }

  public readFrame(gamepad?: GamepadSnapshot | null): InputFrame {
    if (gamepad !== undefined) {
      if (gamepad === null) this.controller.disconnect();
      else this.controller.poll(gamepad);
    }
    return this.input.readFrame();
  }

  public attach(windowTarget: EventTarget, documentTarget: VisibilityEventTarget): () => void {
    this.#detach?.();

    const onKeyDown = (event: Event): void => {
      const keyboardEvent = event as KeyboardEventData;
      if (shouldIgnoreGlobalKeyboard(keyboardEvent.code, event.target as ClosestTarget | null)) return;
      if (this.keyboard.keyDown(keyboardEvent.code, keyboardEvent.repeat)) event.preventDefault();
    };
    const onKeyUp = (event: Event): void => {
      const keyboardEvent = event as KeyboardEventData;
      if (shouldIgnoreGlobalKeyboard(keyboardEvent.code, event.target as ClosestTarget | null)) return;
      if (this.keyboard.keyUp(keyboardEvent.code)) event.preventDefault();
    };
    const onBlur = (): void => {
      this.keyboard.releaseAll();
      this.touch.releaseAll();
      this.#pauseCoordinator.handleWindowFocus(false);
    };
    const onFocus = (): void => this.#pauseCoordinator.handleWindowFocus(true);
    const onVisibility = (): void => {
      if (documentTarget.hidden) {
        this.keyboard.releaseAll();
        this.touch.releaseAll();
        this.controller.suspendUntilNeutral();
      }
      this.#pauseCoordinator.handleVisibility(documentTarget.hidden);
    };

    windowTarget.addEventListener('keydown', onKeyDown);
    windowTarget.addEventListener('keyup', onKeyUp);
    windowTarget.addEventListener('blur', onBlur);
    windowTarget.addEventListener('focus', onFocus);
    documentTarget.addEventListener('visibilitychange', onVisibility);

    let attached = true;
    const detach = (): void => {
      if (!attached) return;
      attached = false;
      windowTarget.removeEventListener('keydown', onKeyDown);
      windowTarget.removeEventListener('keyup', onKeyUp);
      windowTarget.removeEventListener('blur', onBlur);
      windowTarget.removeEventListener('focus', onFocus);
      documentTarget.removeEventListener('visibilitychange', onVisibility);
      this.keyboard.releaseAll();
      this.touch.releaseAll();
      this.controller.disconnect();
      if (this.#detach === detach) this.#detach = undefined;
    };

    this.#detach = detach;
    onVisibility();
    return detach;
  }
}

export const GAME_RUNTIME = new BrowserGameRuntime();
